import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_DISCOUNT_PERCENT } from "@nh/shared";
import { cn } from "@/shared/lib/cn";

export interface PackagesHeroDialProps {
  /**
   * Lado del círculo en escritorio, en píxeles. En pantallas estrechas
   * manda el `clamp` —ver abajo—, no este número.
   */
  size?: number;
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
}

/**
 * El disco de descuento: la pieza viva de la portada de `/paquetes`.
 *
 * ── Por qué NO es el vídeo circular de `/franquicias` ────────────────
 * El encargo de Anahi fue literal: las portadas siguen el patrón de
 * `/franquicias` «con diferentes animaciones a la derecha». `CircleVideo`
 * es la pieza de esa página; repetirla aquí haría que `/paquetes` y
 * `/franquicias` abrieran con el mismo plano y el mismo bucle, y la
 * segunda parecería la primera recargada. Lo que se reutiliza de
 * `CircleVideo` es el CRITERIO —la forma circular, el halo violeta que la
 * despega del fondo, y sobre todo cómo se comporta con movimiento
 * reducido—, no el vídeo.
 *
 * ── Y por qué un disco de descuento y no otra decoración ─────────────
 * Porque el argumento de esta página ES un número. La entradilla dice
 * "hasta un 15%, y con franquicia digital hasta un 30%"; el disco lo
 * enseña. Una portada de catálogo que en la mitad derecha pone una forma
 * bonita desperdicia el único sitio de la página donde todo el mundo
 * mira.
 *
 * ── Los dos porcentajes salen del contrato ───────────────────────────
 * `MAX_DISCOUNT_PERCENT`, el mismo que lee la entradilla. Escritos a mano
 * en el SVG, el día que la banda cambie el disco enseñaría un número y el
 * párrafo de al lado otro, a veinte centímetros de distancia.
 *
 * ── Es decoración: no dice nada nuevo ────────────────────────────────
 * `PageHeroAside` lo saca del árbol de accesibilidad, así que las dos
 * cifras TIENEN que estar también en el texto de la izquierda. Lo están.
 * Si algún día el disco enseñara un dato que la entradilla no cuenta,
 * habría que sacarlo de la caja decorativa y darle su propio texto.
 *
 * ── Movimiento reducido: el disco se ve ENTERO y quieto ──────────────
 * Es el mismo criterio de `CircleVideo` y `ShopHeroVideo`: no se apaga la
 * pieza, se apaga el movimiento. Con la preferencia puesta los dos arcos
 * aparecen ya dibujados en su valor final —sin transición, sin un
 * fotograma de arco a cero— y el disco no flota. Lo que se lee es
 * exactamente lo mismo; solo que no se mueve nada.
 *
 * ── Sin degradados ───────────────────────────────────────────────────
 * Los arcos son trazos de color PLANO y el disco es `ds-ink` sólido. Un
 * disco de descuento pide a gritos un `conic-gradient`, y es justo lo que
 * Anahi retiró del sistema el 26-ago. Las sombras sí se quedan: una
 * sombra no es un relleno de superficie.
 *
 * ── Contraste dentro del disco ───────────────────────────────────────
 * Medido sobre la captura real, no sobre `ds-ink` a secas: las sombras
 * internas aclaran el fondo hasta rgb(44,42,55) por la zona de los
 * rótulos, y ese es el peor punto, no el #1F1D2B teórico.
 *
 *   blanco     #FFFFFF → 14.1 : 1   (el "30%")
 *   ds-lilac   #C7BFDF →  8.0 : 1   (la línea del 15%)
 *   white/70            →  7.7 : 1   (los dos rótulos pequeños)
 *
 * El mínimo para texto pequeño es 4.5 : 1; el peor de los tres lo supera
 * en un 70 %.
 */
export function PackagesHeroDial({ size = 340, motionOk }: PackagesHeroDialProps) {
  const { t } = useTranslation();

  /*
   * El dibujado de los arcos. Arranca en `false` y pasa a `true` en el
   * primer efecto: así el navegador pinta una vez con los arcos a cero,
   * ve cambiar `strokeDashoffset` y ANIMA la transición. Puesto a `true`
   * desde el principio no habría cambio que animar y los arcos
   * aparecerían de golpe.
   *
   * Con movimiento reducido se inicializa ya en `true` y las clases de
   * transición no se ponen: no hay ni animación ni el destello del estado
   * inicial.
   */
  const [drawn, setDrawn] = useState(!motionOk);

  useEffect(() => {
    if (!motionOk) {
      setDrawn(true);
      return;
    }
    /*
     * Dos fotogramas, no uno: con un solo `requestAnimationFrame` el
     * cambio de estado puede caer en el mismo ciclo de pintado que el
     * montaje y el navegador se salta la transición.
     */
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setDrawn(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [motionOk]);

  const franchisee = MAX_DISCOUNT_PERCENT.franchisee;
  const personal = MAX_DISCOUNT_PERCENT.personal;

  return (
    <div
      className={cn("relative shrink-0", motionOk && "animate-float-silk")}
      /*
       * El lado es fluido, no fijo. A 340 px clavados el disco ocupaba
       * casi todo el ancho de un teléfono de 390 y el rótulo de dentro
       * llegaba al borde. `clamp` lo deja en 68 vw mientras eso sea menor
       * que `size`, o sea unos 265 px en ese teléfono, y a partir de
       * tableta se queda en los 340 de escritorio.
       *
       * El SVG no necesita nada: tiene `viewBox`, así que se reescala
       * solo. Lo que sí lo necesita es el texto de dentro, y por eso sus
       * tamaños también van con `clamp` — con píxeles fijos, el disco
       * encogía y las letras no.
       */
      style={{
        width: `clamp(200px, 68vw, ${size}px)`,
        height: `clamp(200px, 68vw, ${size}px)`,
      }}
    >
      <div
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-ds-ink"
        style={{
          /*
           * El mismo halo que `CircleVideo`: es lo que despega el círculo
           * del crema de la página y lo que hace que las dos portadas se
           * lean como la misma familia aunque la pieza sea distinta.
           */
          boxShadow:
            "0 0 60px rgba(92,78,213,0.35), 0 0 20px rgba(92,78,213,0.25), inset -18px -6px 40px rgba(255,255,255,0.07), inset 16px 4px 30px rgba(0,0,0,0.28)",
        }}
      >
        {/*
          `pathLength={100}` reescala el trazo: la longitud del círculo
          pasa a valer 100 pase lo que pase con el radio. Así el arco de
          un 30% es literalmente `strokeDasharray="30 100"` y no una
          circunferencia calculada a mano que hay que rehacer cada vez que
          se toca el radio.
        */}
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 h-full w-full -rotate-90"
          aria-hidden="true"
        >
          {/* Carril y arco exteriores · el techo con franquicia digital. */}
          <circle
            cx="100"
            cy="100"
            r="86"
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="10"
          />
          <circle
            cx="100"
            cy="100"
            r="86"
            fill="none"
            stroke="#5C4FD6"
            strokeWidth="10"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${franchisee} 100`}
            strokeDashoffset={drawn ? 0 : franchisee}
            className={cn(
              motionOk && "transition-[stroke-dashoffset] duration-[1400ms] ease-out",
            )}
          />

          {/* Carril y arco interiores · el techo de la recompra personal. */}
          <circle
            cx="100"
            cy="100"
            r="68"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="5"
          />
          <circle
            cx="100"
            cy="100"
            r="68"
            fill="none"
            stroke="#C7BFDF"
            strokeWidth="5"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${personal} 100`}
            strokeDashoffset={drawn ? 0 : personal}
            className={cn(
              motionOk &&
                "transition-[stroke-dashoffset] delay-200 duration-[1400ms] ease-out",
            )}
          />
        </svg>

        <div className="relative px-[16%] text-center">
          <p className="text-[clamp(8px,2.2vw,10px)] font-bold uppercase leading-none tracking-ds-label-wide text-white/70">
            {t("Ahorra hasta")}
          </p>

          <p className="mt-3 text-[clamp(38px,11vw,52px)] font-light leading-none tracking-tight text-white">
            {franchisee}%
          </p>

          <p className="mt-3 text-[clamp(8px,2.2vw,10px)] font-bold uppercase leading-tight tracking-ds-label-wide text-white/70">
            {t("Con franquicia digital")}
          </p>

          {/* Filete plano: separa las dos cifras sin dibujar una caja. */}
          <div className="mx-auto mt-4 h-px w-14 bg-white/15" />

          <p className="mt-4 text-[clamp(8px,2.2vw,10px)] font-bold uppercase leading-tight tracking-ds-label-wide text-ds-lilac">
            {t("{{personal}}% en recompra personal", { personal })}
          </p>
        </div>
      </div>
    </div>
  );
}
